import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
  Grid,
  Grow,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Lock, Error } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import API from 'api';
import { useNavigation } from 'Hooks/navigate';
import styles from './index.module.scss';
import SignInImg from 'Assets/Images/login.png';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const { goToSignIn } = useNavigation();

  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState(''); // Store userType from API
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordStrengthText, setPasswordStrengthText] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [errorMessage, setErrorMessage] = useState('');

  // Validate token function wrapped in useCallback
  const validateToken = useCallback(async () => {
    try {
      const response = await API.auth.validateResetToken({ token });

      if (response.data.success && response.data.isValid) {
        setTokenValid(true);
        setEmail(response.data.data?.email || emailFromUrl || '');
        setUserType(response.data.data?.userType || 'advisor'); // Store userType from backend
      } else {
        setTokenValid(false);
        setErrorMessage(response.data.message || 'Invalid or expired reset link');
      }
    } catch (error) {
      console.error('Error validating token:', error);
      setTokenValid(false);
      setErrorMessage(error.response?.data?.message || 'Invalid or expired reset link');
    } finally {
      setValidating(false);
    }
  }, [token, emailFromUrl]);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setValidating(false);
      setErrorMessage('No reset token provided');
      return;
    }

    validateToken();
  }, [token, validateToken]);

  // Check password strength
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordStrengthText('');
      return;
    }

    let strength = 0;
    let text = '';

    // Length
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;

    // Contains lowercase and uppercase
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;

    // Contains numbers
    if (/\d/.test(password)) strength += 12.5;

    // Contains special characters
    if (/[^A-Za-z0-9]/.test(password)) strength += 12.5;

    if (strength < 50) {
      text = 'Weak';
    } else if (strength < 75) {
      text = 'Medium';
    } else {
      text = 'Strong';
    }

    setPasswordStrength(strength);
    setPasswordStrengthText(text);
  };

  // Validation schema
  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters'),
    confirmPassword: Yup.string()
      .required('Please confirm your password')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
  });

  const initialValues = {
    newPassword: '',
    confirmPassword: ''
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const response = await API.auth.resetPasswordWithToken({
        token: token,
        newPassword: values.newPassword
      });
      if (response.data.success) {
        setResetSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Countdown and redirect effect for success
  useEffect(() => {
    if (resetSuccess) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            goToSignIn(userType || 'advisor');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resetSuccess, userType]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to right, #041e42, #78a8e6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Grid container spacing={0}>
        {/* Left Side - Info */}
        <Grow in timeout={1000}>
          <Grid item xs={12} md={5} sx={{ color: 'white', p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              RESET YOUR PASSWORD
            </Typography>
            <Typography variant="h5" gutterBottom>
              Trusted Advisory Service
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.8 }}>
              Secure your account with a new password. Make sure to choose a strong password that you haven't used before.
            </Typography>
            <Box component="img" src={SignInImg} alt="illustration" sx={{ width: '60%', maxWidth: 400, mt: 4 }} />
          </Grid>
        </Grow>

        {/* Right Side - Reset Form */}
        <Grid item xs={12} md={7}>
          <Grow in timeout={1000}>
            <Card sx={{ height: '100%', width: "80%", m: "auto", borderRadius: 4, p: 3 }}>
              <CardContent>

                {/* Validating State */}
                {validating && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.iconContainer}>
                      <CircularProgress size={80} thickness={4} />
                      <Lock className={styles.lockIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.title} sx={{ mt: 3, mb: 2 }}>
                      Validating reset link...
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className={styles.subtitle}>
                      Please wait while we verify your reset token
                    </Typography>
                    <Box className={styles.dotsContainer}>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                      <span className={styles.dot}></span>
                    </Box>
                  </Box>
                )}

                {/* Invalid Token State */}
                {!validating && !tokenValid && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.errorIconContainer}>
                      <Error className={styles.errorIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.errorTitle} sx={{ mt: 2, mb: 2 }}>
                      Invalid or Expired Link
                    </Typography>
                    <Box className={styles.errorBox}>
                      <Typography variant="body1" className={styles.errorMessage}>
                        {errorMessage || 'This password reset link is invalid or has expired. Reset links are valid for 1 hour.'}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      className={styles.actionButton}
                      onClick={() => goToSignIn(userType || 'advisor')}
                      fullWidth
                      sx={{ mt: 3 }}
                    >
                      Go to Login
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Need a new reset link? Try the "Forgot Password" option on the login page.
                    </Typography>
                  </Box>
                )}

                {/* Success State */}
                {resetSuccess && (
                  <Box className={styles.centerContent}>
                    <Box className={styles.successIconContainer}>
                      <CheckCircle className={styles.successIcon} />
                    </Box>
                    <Typography variant="h4" className={styles.successTitle} sx={{ mt: 2, mb: 2 }}>
                      Password Reset Successfully!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" className={styles.message} sx={{ mb: 3 }}>
                      Your password has been updated successfully. You can now login with your new password.
                    </Typography>
                    <Box className={styles.redirectBox}>
                      <Typography variant="body1" className={styles.redirectText}>
                        <span className={styles.bold}>Redirecting to login page in {countdown} seconds</span>
                      </Typography>
                      <CircularProgress
                        size={24}
                        sx={{ mt: 2, color: '#1565c0' }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      className={styles.successButton}
                      onClick={() => goToSignIn(userType || 'advisor')}
                      fullWidth
                      sx={{ mt: 3 }}
                    >
                      Go to Login Now
                    </Button>
                  </Box>
                )}

                {/* Reset Password Form */}
                {!validating && tokenValid && !resetSuccess && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
                      Reset Your Password
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                      Enter your new password below
                    </Typography>

                    {/* Display Email (read-only) */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Resetting password for:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {email}
                      </Typography>
                    </Box>

                    <Formik
                      initialValues={initialValues}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {(formikProps) => (
                        <Form>
                          {/* New Password */}
                          <TextField
                            fullWidth
                            label="New Password"
                            margin="normal"
                            name="newPassword"
                            placeholder="Enter new password"
                            type={showPassword ? 'text' : 'password'}
                            value={formikProps.values.newPassword}
                            onChange={(e) => {
                              formikProps.setFieldValue('newPassword', e.target.value);
                              checkPasswordStrength(e.target.value);
                            }}
                            onBlur={formikProps.handleBlur}
                            error={formikProps.touched.newPassword && Boolean(formikProps.errors.newPassword)}
                            helperText={formikProps.touched.newPassword && formikProps.errors.newPassword}
                            disabled={loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />

                          {/* Password Strength Indicator */}
                          {formikProps.values.newPassword && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Password Strength:
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 'bold',
                                    color:
                                      passwordStrength < 50
                                        ? '#f44336'
                                        : passwordStrength < 75
                                          ? '#ff9800'
                                          : '#4caf50'
                                  }}
                                >
                                  {passwordStrengthText}
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={passwordStrength}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor:
                                      passwordStrength < 50
                                        ? '#f44336'
                                        : passwordStrength < 75
                                          ? '#ff9800'
                                          : '#4caf50'
                                  }
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Use at least 8 characters with uppercase, lowercase, numbers, and symbols
                              </Typography>
                            </Box>
                          )}

                          {/* Confirm Password */}
                          <TextField
                            fullWidth
                            label="Confirm Password"
                            margin="normal"
                            name="confirmPassword"
                            placeholder="Confirm new password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={formikProps.values.confirmPassword}
                            onChange={(e) => formikProps.setFieldValue('confirmPassword', e.target.value)}
                            onBlur={formikProps.handleBlur}
                            error={formikProps.touched.confirmPassword && Boolean(formikProps.errors.confirmPassword)}
                            helperText={formikProps.touched.confirmPassword && formikProps.errors.confirmPassword}
                            disabled={loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />

                          <Box sx={{ mt: 3 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              type="submit"
                              disabled={loading || !formikProps.isValid}
                              sx={{
                                py: 1.5,
                                fontSize: '16px',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                                boxShadow: 2,
                                '&:hover': {
                                  boxShadow: 4,
                                },
                              }}
                            >
                              {loading ? (
                                <>
                                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                  Resetting Password...
                                </>
                              ) : (
                                'Reset Password'
                              )}
                            </Button>
                          </Box>
                        </Form>
                      )}
                    </Formik>
                  </Box>
                )}

              </CardContent>
            </Card>
          </Grow>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPassword;
